import { useCallback } from 'react';
import { useRouteMatch } from 'react-router';

import { AiAssistantEvent, AiAssistantTranscriptionReceivedEvent } from '@dreryk/egabinet-ai-assistant-frontend-api';

import { useCqrsEvent } from '@dreryk/command-query-react-common';

import { visitPaths } from '~libs.desktop.shared.paths';

import { useAiAssistantVisitSummaryContext } from '../shared/AiAssistantVisitSummary.context';
import { useAiAssistantRecordingGlobalState } from '../shared/useAiAssistantRecordingGlobalState';

const AI_ASSISTANT_WEB_EVENT_MOBILE_RECORDING = 'mobileRecording';
const AI_ASSISTANT_WEB_EVENT_MOBILE_PENDING = 'mobilePending';
const AI_ASSISTANT_WEB_EVENT_IDLE = 'idle';
const AI_ASSISTANT_WEB_EVENT_FINAL = 'final';
const AI_ASSISTANT_WEB_EVENT_VALUE_SEPARATOR = ':';

const parseAccumulatedSeconds = (eventValue: string | undefined): number => {
   const accumulatedSeconds = Number(eventValue);

   return Number.isFinite(accumulatedSeconds) && accumulatedSeconds > 0 ? accumulatedSeconds : 0;
};

export const AiAssistantMobileRecordingEventListener = (): null => {
   const visitRouteMatch = useRouteMatch<{ visitId: string }>(visitPaths.VISIT_PATH);
   const visitId = Number(visitRouteMatch?.params.visitId);
   const {
      startMobileRecording,
      generateMobileNote,
      updateMobileNoteDuration,
      resetMobileRecording,
   } = useAiAssistantRecordingGlobalState();
   const { summarizeMobileTranscript } = useAiAssistantVisitSummaryContext();

   const handleAiAssistantEvent = useCallback(
      (event: AiAssistantEvent) => {
         if (event.visitId !== visitId) {
            return;
         }

         const [eventName, eventValue] = event.event.split(AI_ASSISTANT_WEB_EVENT_VALUE_SEPARATOR);

         if (eventName === AI_ASSISTANT_WEB_EVENT_MOBILE_RECORDING) {
            startMobileRecording();
         }

         if (eventName === AI_ASSISTANT_WEB_EVENT_MOBILE_PENDING) {
            generateMobileNote();
         }

         if (eventName === AI_ASSISTANT_WEB_EVENT_FINAL) {
            updateMobileNoteDuration(parseAccumulatedSeconds(eventValue));
         }

         if (eventName === AI_ASSISTANT_WEB_EVENT_IDLE) {
            resetMobileRecording();
         }
      },
      [visitId, startMobileRecording, generateMobileNote, updateMobileNoteDuration, resetMobileRecording]
   );

   const handleTranscriptionReceivedEvent = useCallback(
      (event: AiAssistantTranscriptionReceivedEvent) => {
         if (event.visitId === visitId) {
            summarizeMobileTranscript(event.transcript);
         }
      },
      [visitId, summarizeMobileTranscript]
   );

   useCqrsEvent<AiAssistantEvent>('egabinet-ai-assistant-frontend-api:AiAssistantEvent', handleAiAssistantEvent);
   useCqrsEvent<AiAssistantTranscriptionReceivedEvent>(
      'egabinet-ai-assistant-frontend-api:AiAssistantTranscriptionReceivedEvent',
      handleTranscriptionReceivedEvent
   );

   return null;
};
